<template>
  <div class="cfc mb-4">
    <div class="cfc-head">
      <div>
        <h6 class="mb-0"><i class="fe fe-home mr-2"></i>Claims by facility</h6>
        <small class="text-muted">
          {{ loading ? "Loading…" : `${rows.length} ${rows.length === 1 ? "facility" : "facilities"} · ${money(totalClaims)} claims · ₦${money(totalAmount)}` }}
        </small>
      </div>
      <input v-if="rows.length > 6" v-model="search" type="search" class="form-control form-control-sm cfc-search" placeholder="Find a facility…" />
    </div>

    <p v-if="error" class="text-danger small mb-0">{{ error }}</p>
    <p v-else-if="!loading && !shown.length" class="text-muted small mb-0">
      {{ rows.length ? "No facility matches your search." : "No facility has claims for this period." }}
    </p>

    <div v-else class="cfc-scroller">
      <button type="button" class="cfc-arrow" title="Scroll left" @click="scroll(-1)"><i class="fe fe-chevron-left"></i></button>
      <div ref="strip" class="cfc-strip">
        <button
          v-for="row in shown"
          :key="row.provider_id"
          type="button"
          class="cfc-card"
          :class="{ active: selectedId === row.provider_id }"
          :title="row.agency_name"
          @click="toggle(row)"
        >
          <span class="cfc-name">{{ row.agency_name }}</span>
          <span class="cfc-figure">{{ money(row.total_claims) }} <small>claims</small></span>
          <span class="cfc-amount">₦{{ money(row.total_amount) }}</span>
          <span v-if="statusLabel" class="cfc-chip" :class="{ none: !statusCount(row) }">{{ money(statusCount(row)) }} {{ statusLabel.toLowerCase() }}</span>
        </button>
      </div>
      <button type="button" class="cfc-arrow" title="Scroll right" @click="scroll(1)"><i class="fe fe-chevron-right"></i></button>
    </div>

    <div v-if="selected" class="cfc-info">
      <div class="cfc-info-head">
        <strong>{{ selected.agency_name }}</strong>
        <button type="button" class="btn btn-sm btn-link text-muted p-0" @click="$emit('select', null)">
          <i class="fe fe-x mr-1"></i>All facilities
        </button>
      </div>
      <p v-if="!selectedRow" class="text-muted small mb-0 mt-2">No claims for this period.</p>
      <div v-else class="cfc-tiles">
        <button type="button" class="cfc-tile total" :class="{ active: !status }" @click="$emit('status', '')">
          <span class="cfc-tile-label">Total</span>
          <span class="cfc-tile-count">{{ money(selectedRow.total_claims) }}</span>
          <span class="cfc-tile-amount">₦{{ money(selectedRow.total_amount) }}</span>
        </button>
        <button
          v-for="s in statuses"
          :key="s.key"
          type="button"
          class="cfc-tile"
          :class="{ active: status === s.key }"
          @click="$emit('status', s.key)"
        >
          <span class="cfc-tile-label">{{ s.label }}</span>
          <span class="cfc-tile-count">{{ money(figure(s.key).claims) }}</span>
          <span class="cfc-tile-amount">₦{{ money(figure(s.key).amount) }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const STATUSES = [
  { key: "pending", label: "Pending" },
  { key: "vetted", label: "Vetted" },
  { key: "verified", label: "Verified" },
  { key: "forwarded", label: "Forwarded to ES" },
  { key: "approved", label: "Approved" },
  { key: "sent_back", label: "Sent back" },
  { key: "suspended", label: "Suspended" },
  { key: "rejected", label: "Rejected" },
];

export default {
  name: "ClaimFacilityCards",
  props: {
    params: { type: Object, default: null }, // from, to, sector, phc_type, phc_general
    facility: { type: [Object, String], default: "" }, // the page's chosen facility ({ id, agency_name })
    status: { type: String, default: "" }, // the page's status filter
  },
  data() {
    return { rows: [], loading: false, error: "", search: "", statuses: STATUSES, requestId: 0 };
  },
  computed: {
    selectedId() {
      return this.facility && this.facility.id ? Number(this.facility.id) : null;
    },
    selectedRow() {
      return this.rows.find((row) => row.provider_id === this.selectedId) || null;
    },
    selected() {
      return this.selectedId ? this.selectedRow || this.facility : null;
    },
    statusLabel() {
      const status = STATUSES.find((s) => s.key === this.status);
      return status ? status.label : "";
    },
    // Facilities with claims at the chosen status first, so a verifier sees where the vetted claims are.
    shown() {
      const term = this.search.trim().toLowerCase();
      return this.rows
        .filter((row) => !term || row.agency_name.toLowerCase().includes(term))
        .sort((a, b) => this.statusCount(b) - this.statusCount(a) || b.total_claims - a.total_claims);
    },
    totalClaims() {
      return this.rows.reduce((sum, row) => sum + row.total_claims, 0);
    },
    totalAmount() {
      return this.rows.reduce((sum, row) => sum + row.total_amount, 0);
    },
  },
  watch: {
    params: { immediate: true, handler: "load" },
  },
  methods: {
    load() {
      if (!this.params) return;
      const id = ++this.requestId;
      this.loading = true;
      this.error = "";
      this.axios
        .get("/api/v1/auth/claims/facility-summary", { params: this.params })
        .then((response) => {
          if (id === this.requestId) this.rows = response.data.data || [];
        })
        .catch((error) => {
          console.error(error);
          if (id === this.requestId) this.error = "Could not load the facilities.";
        })
        .finally(() => {
          if (id === this.requestId) this.loading = false;
        });
    },
    statusCount(row) {
      const figure = row.statuses[this.status];
      return figure ? figure.claims : 0;
    },
    figure(key) {
      return this.selectedRow.statuses[key] || { claims: 0, amount: 0 };
    },
    toggle(row) {
      this.$emit("select", this.selectedId === row.provider_id ? null : row);
    },
    scroll(direction) {
      this.$refs.strip.scrollBy({ left: direction * 480, behavior: "smooth" });
    },
    money(value) {
      return Number(value || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 });
    },
  },
};
</script>

<style scoped>
.cfc { background: #fff; border-radius: 12px; padding: 16px 20px; box-shadow: 0 1px 3px rgba(15, 23, 42, .08); }
.cfc-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.cfc-head h6 { font-weight: 600; color: #1e3a5f; }
.cfc-search { max-width: 220px; }

.cfc-scroller { display: flex; align-items: center; gap: 6px; }
.cfc-strip { display: flex; gap: 12px; overflow-x: auto; flex: 1; min-width: 0; padding: 2px 2px 8px; scroll-snap-type: x proximity; }
.cfc-arrow {
  flex: none; width: 28px; height: 28px; border-radius: 50%; border: 1px solid #e2e8f0;
  background: #fff; color: #475569; cursor: pointer; line-height: 1; padding: 0;
}
.cfc-arrow:hover { background: #f1f5f9; }

.cfc-card {
  flex: 0 0 200px; display: flex; flex-direction: column; align-items: flex-start; gap: 2px;
  text-align: left; padding: 12px 14px; background: #fff; cursor: pointer;
  border: 1.5px solid #e2e8f0; border-radius: 10px; scroll-snap-align: start;
  transition: border-color .15s, box-shadow .15s;
}
.cfc-card:hover { border-color: #93c5fd; box-shadow: 0 2px 10px rgba(37, 99, 235, .1); }
.cfc-card.active { border-color: #2563eb; background: #eff6ff; }
.cfc-name {
  font-size: .82rem; font-weight: 600; color: #1e3a5f; line-height: 1.25; min-height: 2.5em;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.cfc-figure { font-size: 1.2rem; font-weight: 700; color: #0f172a; }
.cfc-figure small { font-size: .72rem; font-weight: 500; color: #64748b; }
.cfc-amount { font-size: .8rem; color: #475569; }
.cfc-chip { margin-top: 6px; padding: 2px 8px; border-radius: 20px; font-size: .7rem; font-weight: 600; background: #dbeafe; color: #1d4ed8; }
.cfc-chip.none { background: #f1f5f9; color: #94a3b8; }

.cfc-info { margin-top: 12px; padding-top: 12px; border-top: 1px solid #f1f5f9; }
.cfc-info-head { display: flex; align-items: center; justify-content: space-between; color: #1e3a5f; }
.cfc-tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(100px, 1fr)); gap: 10px; margin-top: 10px; }
.cfc-tile {
  display: flex; flex-direction: column; align-items: flex-start; padding: 10px 12px; background: #f8fafc;
  border: 1.5px solid transparent; border-radius: 8px; cursor: pointer; text-align: left;
}
.cfc-tile:hover { border-color: #93c5fd; }
.cfc-tile.active { border-color: #2563eb; background: #eff6ff; }
.cfc-tile.total { background: #1e3a5f; color: #fff; }
.cfc-tile.total .cfc-tile-label, .cfc-tile.total .cfc-tile-amount, .cfc-tile.total .cfc-tile-count { color: #fff; }
.cfc-tile-label { font-size: .7rem; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #64748b; }
.cfc-tile-count { font-size: 1.15rem; font-weight: 700; color: #0f172a; }
.cfc-tile-amount { font-size: .75rem; color: #475569; }
</style>
